import React, {Component} from 'react';
import {withTranslation} from "react-i18next";
import NavigationProvider from "v4/features/front/navigation/contexts/NavigationContext";
import Layout from "../../components/Layout/layout";
import {Router as RouterDom} from 'react-router-dom';
import {createBrowserHistory} from 'history';
import Popup from "../../components/Popup/popup";
import ErrorPopin from "../../components/Error/errorPopin";
import {setPopup} from "../../actions/content";
import {connect} from "react-redux";
import GlobalErrorBoundary from "../Error/index";
import {GlobalFonts} from '../../fonts/globalFonts'
import {RecoilRoot} from 'recoil';
import MercureComponent from '../../components/Mercure/mercure';
import AuthenticationChecker from "components/AuthenticationChecker/AuthenticationChecker";
import * as Sentry from "@sentry/react";
import {MAINTENANCE_HEADER, TRANSACTION_ID_HEADER} from "v4/contexts/AuthContext";
import MercureProvider from "v4/contexts/MercureContext";
import AppVersionNotice from "v4/features/front/appVersion/AppVersionNotice";

// Shared history instance so Sentry's React Router v5 instrumentation can name
// navigation transactions from the destination route (instead of browserTracing's
// default, which reads window.location at the raw history event and mislabels the
// transaction with the route you navigated *from*).
const history = createBrowserHistory();

if (!Sentry.isInitialized()) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
        release: process.env.REACT_APP_SENTRY_RELEASE,

        // When in prod, when no error happens, we don't care about replays
        // But in dev, we want to capture all replays
        replaysSessionSampleRate: 0,

        // However, on error it will force a replay to be saved and sent to Sentry
        replaysOnErrorSampleRate: 1.0,

        // All errors MUST be sent to Sentry
        tracesSampleRate: 1.0,

        integrations: [
            Sentry.reactRouterV5BrowserTracingIntegration({
                // Route navigation transactions through the shared history so they
                // are named from the destination route, at the correct time.
                history,
                // Capture same-page user interactions (clicks/input) as INP spans,
                // not just pageload + route-change transactions.
                enableInp: true,
                // Don't create spans for the dev Symfony profiler/debugbar fetches.
                // The debugbar fires a /dev/profiler/<token> request after every API
                // call, which would otherwise keep resetting the idle timeout and
                // prevent the route transaction from ever settling (so it never flushes).
                shouldCreateSpanForRequest: (url) => !url.includes('/dev/profiler/'),
                tracePropagationTargets: [
                    "localhost",
                    process.env.REACT_APP_PUBLIC_URL,
                    process.env.REACT_APP_HOST_API,
                    process.env.REACT_APP_POL_CORE_URL,
                    process.env.REACT_APP_API_SERVICE_PLENETUDE_HOST,
                ]
            }),
            Sentry.replayIntegration({
                // Do not keep track of users across page loads (SPA)
                stickySession: false,

                // Allow request/response saving from & to middleware app
                networkDetailAllowUrls: [
                    window.location.origin,
                    process.env.REACT_APP_HOST_API,
                    `${process.env.REACT_APP_API_SERVICE_PLENETUDE_HOST}/quote/before-save` // TODO: remove this when https://sfimultimedia.atlassian.net/browse/PV-1789 is fixed
                ],
                networkRequestHeaders: [
                    TRANSACTION_ID_HEADER
                ],
                networkResponseHeaders: [
                    MAINTENANCE_HEADER
                ],
                networkCaptureBodies: true,

                maxReplayDuration: 60 * 1000,

                // Privacy
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
            }),
            Sentry.replayCanvasIntegration(),
        ],
    });
}

class App extends Component {

    render() {
        let classLoader = '',
            classGeneral = '';
        const {loader, className = '', error} = this.props;
        const loaderKeys = Object.keys(loader);

        loaderKeys.map((item) => {
            if (loader[item]) {
                if (classLoader === '') {
                    classLoader = 'is-loading'
                }
                classLoader += ` loading-${item}`
            }
            return '';
        });

        classGeneral += className;

        return (
            <RecoilRoot>
                <GlobalErrorBoundary>
                    <div className={`${classLoader} ${classGeneral}`}>
                        <GlobalFonts/>
                        <RouterDom history={history}>
                            <AuthenticationChecker>
                                <MercureProvider hubUrl={process.env.REACT_APP_MERCURE_HUB_URL}
                                                 prefix={process.env.REACT_APP_MERCURE_ENV_PREFIX}
                                >
                                    <MercureComponent>
                                        <NavigationProvider>
                                            <Layout/>
                                        </NavigationProvider>
                                        <Popup/>
                                        <AppVersionNotice/>
                                        {error ? <ErrorPopin/> : null}
                                    </MercureComponent>
                                </MercureProvider>
                            </AuthenticationChecker>
                        </RouterDom>
                    </div>
                </GlobalErrorBoundary>
            </RecoilRoot>
        )
    }
}

const mapStateToProps = ({content: {loader, className}, datas: {error}}) => ({
    loader,
    className,
    error
});

const mapDispatchToProps = dispatch => {
    return {
        togglePopup: (popupInfo) => dispatch(setPopup(popupInfo)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withTranslation()(App));
